import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/clients";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Send, Trash2, Loader2, Mail, Reply } from "lucide-react";
import { toast } from "sonner";

export function ClientNotes({ clientId, onReply }) {
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadNotes = useCallback(() => {
    api.get(`/clients/${clientId}/notes`).then((r) => setNotes(r.data));
  }, [clientId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      await api.post(`/clients/${clientId}/notes`, { content: noteText.trim() });
      setNoteText("");
      toast.success("Note added");
      loadNotes();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/notes/${deleteTarget.id}`);
      toast.success("Note deleted");
      loadNotes();
    } catch { toast.error("Could not delete note"); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6">
      <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Notes & activity</h3>

      <form onSubmit={addNote} className="mb-6">
        <textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3}
          placeholder="Log a call, meeting, or note…"
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-none" />
        <div className="flex justify-end mt-2">
          <button type="submit" disabled={posting || !noteText.trim()} data-testid="add-note-button"
            className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors disabled:opacity-50">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Add note
          </button>
        </div>
      </form>

      {notes.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">No notes yet. Add the first one above.</p>
      ) : (
        <div className="relative pl-6 border-l-2 border-gray-200 ml-2 space-y-5" data-testid="notes-timeline">
          {notes.map((n) => (
            <div key={n.id} className="relative group" data-testid={`note-${n.id}`}>
              <span className={`absolute -left-[31px] top-1.5 w-3.5 h-3.5 rounded-full border-4 border-white ${n.kind === "email" ? "bg-emerald-600" : "bg-[#002FA7]"}`} />
              <div className="bg-gray-50 border border-gray-100 p-4 rounded-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                    {n.kind === "email" && <Mail className="w-3.5 h-3.5 text-emerald-600" />}
                    {n.author_name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{formatDateTime(n.created_at)}</span>
                    {n.kind === "email" && n.email && onReply && (
                      <button onClick={() => onReply(n.email)} data-testid={`reply-note-${n.id}`}
                        title="Reply" className="text-gray-400 hover:text-[#002FA7] transition-colors flex items-center gap-0.5 text-xs font-semibold">
                        <Reply className="w-3.5 h-3.5" /> Reply
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget(n)} data-testid={`delete-note-${n.id}`}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{n.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete note?</AlertDialogTitle>
            <AlertDialogDescription>This note will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-note-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
